import { useState, useCallback, useMemo } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { PanelToGenesMappingDataType, SearchMode } from '../_types';
import { MAX_QUERY_LENGTH, validateQueryParam } from '../_utils';
import { useGeneCoverageData } from './useGeneCoverageData';
import { useSearchFilteredGenes } from './useSearchFilteredGenes';
import { useCsvToTrieMap } from './useCsvToTrieMap';
import { usePanelToGenesMappingData } from './usePanelToGenesMappingData';

// 기존 '../_hooks' 경유 소비처 호환을 위해 재노출
export { MAX_QUERY_LENGTH };

// 매핑 데이터 로딩 전 기본값 — 매 렌더 새 객체가 생기면 하위 useMemo 의존성이 깨지므로 고정 참조를 쓴다.
const EMPTY_PANEL_MAP: PanelToGenesMappingDataType = {};

/**
 * 유전자 검색 및 데이터 필터링 기능을 통합 관리하는 커스텀 훅
 *
 * URL 쿼리 파라미터(query, mode)를 검색의 유일한 source of truth 로 삼는다.
 * 검색 상태를 별도 state 로 복제하지 않고 URL 에서 매 렌더마다 파생하므로,
 * 뒤로가기·링크 직접 진입·초기화는 searchParams 변경에 따른 리렌더로 자연히 반영된다.
 *
 * @returns {Object} 유전자 검색 및 데이터 필터링 관련 통합된 상태와 함수
 */
export function useGeneSearchManager() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // 입력이 1000자를 초과해 검색을 실행하지 못했을 때 안내 표시 (순수 로컬 UI 상태)
  const [isQueryTooLong, setIsQueryTooLong] = useState(false);

  // URL(검색)이 바뀌면 직전 '검색어 너무 김' 안내는 무의미하므로 리셋한다.
  // too-long 시도는 URL 을 갱신하지 않으므로 queryKey 가 그대로여서 안내가 유지되고,
  // 뒤로/앞으로 가기(soft nav)로 URL 이 바뀌면 이 비교가 걸려 안내가 사라진다.
  const queryKey = searchParams.toString();
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
  if (queryKey !== prevQueryKey) {
    setPrevQueryKey(queryKey);
    setIsQueryTooLong(false);
  }

  // 유전자 데이터 가져오기 (CSV 텍스트)
  const { data: csvText } = useGeneCoverageData();

  // 패널-유전자 매핑 데이터 가져오기
  const { data: panelToGenesMappingData = EMPTY_PANEL_MAP } =
    usePanelToGenesMappingData();

  // CSV 텍스트를 파싱하여 geneDataMap과 geneTrie 생성
  const allGeneData = useCsvToTrieMap(csvText || '');

  // CSV 가 아직 없으면(초기 페치·영속 캐시 복원 중) 로딩으로 취급한다.
  // React Query 복원 중엔 isLoading 이 false 라도 데이터가 없을 수 있어 data 유무로 판단.
  // 이 가드가 없으면 빈 데이터에 검색이 실행돼 유효 유전자를 invalid 로 오판한다.
  const isDataLoading = !csvText;

  // URL 에서 파생한 검색 상태 (검증은 mode 에 따라 달라지므로 mode 를 먼저 구한다)
  const query = searchParams.get('query');
  const searchMode: SearchMode =
    searchParams.get('mode') === 'Panel' ? 'Panel' : 'Genes';
  const urlSearchTerm = validateQueryParam(query, searchMode) ? query : '';
  const hasSearched = urlSearchTerm !== '';

  // 필터링에 쓸 검색어. Panel 모드면 패널 이름을 유전자 목록으로 변환한다.
  const searchTerm = useMemo(() => {
    if (
      searchMode === 'Panel' &&
      urlSearchTerm &&
      Array.isArray(panelToGenesMappingData[urlSearchTerm])
    ) {
      return panelToGenesMappingData[urlSearchTerm].join(',');
    }
    return urlSearchTerm;
  }, [searchMode, urlSearchTerm, panelToGenesMappingData]);

  // 검색어로 필터링된 결과와 유효하지 않은 유전자 이름 목록.
  // 검색은 Trie 조회라 데이터셋 크기·검색량과 무관하게 빠르므로(최악 2000건도 ~2ms),
  // 지연 값이나 스켈레톤 없이 즉시 필터링한다. 로딩은 초기 CSV 페치 때만 필요하다.
  const {
    searchFilteredGeneData,
    searchedInvalidGenes,
    prefixMatchedGenes,
    searchedPrefixes,
  } = useSearchFilteredGenes({
    searchTerm,
    hasSearched,
    allGeneData,
    searchMode,
  });

  // 검색 트리거 함수. URL 만 갱신하면 파생 상태가 따라온다.
  const triggerSearch = useCallback(
    (searchInput: string, mode: SearchMode = 'Genes') => {
      // 원문 입력이 1000자를 초과하면 검색을 실행하지 않고 안내만 표시한다.
      // (Panel 모드는 searchInput 이 짧은 패널 이름이라 제한에 걸리지 않는다)
      if (searchInput.length > MAX_QUERY_LENGTH) {
        setIsQueryTooLong(true);
        return;
      }
      setIsQueryTooLong(false);

      // 검색어가 없으면 쿼리 파라미터 제거
      if (!searchInput) {
        router.push(pathname, { scroll: false });
        return;
      }

      // 읽기(파생)와 같은 기준으로 검증해, URL 에 썼다가 재검증에 걸려 결과가
      // 사라지는 round-trip 불일치를 원천 차단한다. (검증 실패 시 URL 미갱신)
      if (!validateQueryParam(searchInput, mode)) return;

      const params = new URLSearchParams(searchParams.toString());
      params.set('query', searchInput);
      params.set('mode', mode);
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [searchParams, router, pathname],
  );

  const closeQueryTooLongAlert = useCallback(
    () => setIsQueryTooLong(false),
    [],
  );

  return {
    // 검색 결과 및 상태
    searchFilteredGeneData,
    hasSearched,
    searchedInvalidGenes,
    isDataLoading,
    prefixMatchedGenes,
    searchedPrefixes,

    // 1000자 초과 안내 상태
    isQueryTooLong,
    closeQueryTooLongAlert,

    // 검색 관련 상태 (URL 동기화용)
    urlSearchTerm,
    searchMode,

    // 검색 함수
    triggerSearch,
  };
}
